import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import axios from 'axios'
import { useFormik } from 'formik'
import OpportunityResults from '../components/opportunityEngine/OpportunityResults'
import FieldError from '../components/productionTools/FieldError'
import AiToolPaymentErrorBanner from '../components/productionTools/AiToolPaymentErrorBanner'
import '../css/OpportunityEngine.css'
import { genderOptions } from '../common/constants'
import { mapOpportunityEngineOutput } from '../utils/opportunityEngine/mapApiOutput'
import ProductionToolsHeader from '../components/productionTools/ProductionToolsHeader'
import ProductionToolsSelect from '../components/productionTools/ProductionToolsSelect'
import AiToolSamplePdfButton from '../components/productionTools/AiToolSamplePdfButton'
import AiToolRunButton from '../components/productionTools/AiToolRunButton'
import AiToolPriceBadge from '../components/productionTools/AiToolPriceBadge'
import { useAiToolPayment } from '../hooks/useAiToolPayment'
import { useAiToolPaymentError } from '../hooks/useAiToolPaymentError'
import { useAiToolEmbeddedPaymentRestore } from '../hooks/useAiToolEmbeddedPaymentRestore'
import {
  AI_TOOL_TYPES,
  buildOpportunityEngineFormData,
  checkOpportunityEngineAvailability,
} from '../utils/aiTool/payment'
import {
  AI_TOOL_LIMITS,
  getFormBannerError,
  opportunityEngineSchema,
} from '../utils/aiTool/validationSchemas'
import { Stack } from '@mui/material'
import { closeSnackbar, enqueueSnackbar } from 'notistack'
import { getAiToolApiConfig } from '../utils/aiTool/apiConfig'

const STREAM_MESSAGES = [
  'Scanning PitchBlu...',
  'Analyzing opportunities...',
  'Matching posts to your profile...',
  'Scoring and ranking your top 5...',
  'Preparing your results...',
]

const INITIAL_VALUES = {
  email: '',
  uname: '',
  selCrafts: [],
  selCT: [],
  country: 'any',
  state: 'any',
  city: 'any',
  lang: 'any',
  recency: 'any',
  gender: 'any',
  age: '',
}

const ChipSelect = ({ options, selected, onChange }) => (
  <div className="chips">
    {options.map((option) => (
      <button
        key={option}
        type="button"
        className={`chip ${selected.includes(option) ? 'on' : ''}`}
        onClick={() => {
          const idx = selected.indexOf(option)
          if (idx > -1) onChange(selected.filter((s) => s !== option))
          else onChange([...selected, option])
        }}
      >
        {option}
      </button>
    ))}
  </div>
)

const OpportunityEngine = () => {
  const [error, setError] = useState('')
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [streamMsg, setStreamMsg] = useState(STREAM_MESSAGES[0])
  const [result, setResult] = useState(null)
  const [resultInputs, setResultInputs] = useState(null)
  const [professionsList, setProfessionsList] = useState([])
  const [contentTypesList, setContentTypesList] = useState([])
  const [languageList, setLanguageList] = useState([])
  const [countryList, setCountryList] = useState([])
  const [stateList, setStateList] = useState([])
  const [cityList, setCityList] = useState([])
  const { initiatePayment } = useAiToolPayment()
  const resultsRef = useRef(null)
  const msgTimerRef = useRef(null)

  const clearStreamTimer = useCallback(() => {
    if (msgTimerRef.current) {
      clearInterval(msgTimerRef.current)
      msgTimerRef.current = null
    }
  }, [])

  const startStreamTimer = useCallback(() => {
    clearStreamTimer()
    setStreamMsg(STREAM_MESSAGES[0])
    let msgIdx = 0
    msgTimerRef.current = setInterval(() => {
      msgIdx = Math.min(STREAM_MESSAGES.length - 1, msgIdx + 1)
      setStreamMsg(STREAM_MESSAGES[msgIdx])
    }, 2000)
  }, [clearStreamTimer])

  useEffect(() => () => clearStreamTimer(), [clearStreamTimer])

  const showErr = (msg) => {
    setError(msg)
    setTimeout(() => setError(''), 7000)
  }

  const handleEmbeddedRestore = useCallback((completed) => {
    const analysis = mapOpportunityEngineOutput(
      completed.aiResult?.data?.output,
    )

    if (!analysis?.opps?.length) {
      showErr(
        completed.aiResult?.message ||
          'No matches found. Try broadening your filters.',
      )
      return
    }

    const formData = completed.formData || {}
    setResult(analysis)
    setResultInputs({
      email: completed.email,
      uname: completed.fullName,
      crafts: formData.crafts || [],
      ct: formData.contentTypes || [],
      country: formData.country,
      city: formData.city,
      lang: formData.language,
      aiuId: completed.aiResult?.data?.aiuId,
      pdfContent: completed.aiResult?.data?.pdfContent,
    })
    setPaymentLoading(false)
    setAnalyzing(false)
  }, [])

  useAiToolEmbeddedPaymentRestore(
    AI_TOOL_TYPES.OPPORTUNITY_ENGINE,
    handleEmbeddedRestore,
  )

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: opportunityEngineSchema,
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: async (values) => {
      closeSnackbar()
      clearPaymentError()
      setError('')
      setPaymentLoading(true)
      setAnalyzing(false)
      setResult(null)

      try {
        const email = values.email.trim()
        const fullName = values.uname.trim()
        const formData = buildOpportunityEngineFormData({
          uname: fullName,
          selCrafts: values.selCrafts,
          selCT: values.selCT,
          country: values.country,
          state: values.state,
          city: values.city,
          lang: values.lang,
          recency: values.recency,
          gender: values.gender,
          age: values.age.trim(),
        })

        const availability = await checkOpportunityEngineAvailability({
          email,
          fullName,
          formData,
        })

        if (!availability?.canProceedToPayment) {
          enqueueSnackbar('No matching posts are found', {
            variant: 'error',
            autoHideDuration: 2000,
          })
          return
        }

        const paymentResult = await initiatePayment({
          toolType: AI_TOOL_TYPES.OPPORTUNITY_ENGINE,
          email,
          fullName,
          formData,
          rawFormValues: values,
          onPaymentVerified: () => {
            setPaymentLoading(false)
            setAnalyzing(true)
            startStreamTimer()
          },
          onDismiss: () => {
            setPaymentLoading(false)
          },
        })

        if (paymentResult?.redirecting) {
          return
        }

        const { aiResult } = paymentResult

        const analysis = mapOpportunityEngineOutput(aiResult?.data?.output)
        if (!analysis?.opps?.length) {
          throw new Error(
            aiResult?.message ||
              'No matches found. Try broadening your filters.',
          )
        }

        const inputs = {
          email,
          uname: fullName,
          crafts: values.selCrafts,
          ct: values.selCT,
          country: values.country,
          city: values.city,
          lang: values.lang,
          aiuId: aiResult.data?.aiuId,
          pdfContent: aiResult.data?.pdfContent,
        }

        setResult(analysis)
        setResultInputs(inputs)
      } catch (err) {
        if (err.message === 'Payment cancelled') {
          return
        }
        showErr(
          err?.response?.data?.message ||
            err.message ||
            'Something went wrong. Please try again.',
        )
      } finally {
        clearStreamTimer()
        setAnalyzing(false)
        setPaymentLoading(false)
      }
    },
  })

  const { setValues: setFormValues } = formik

  const handleRestoreForm = useCallback(
    (restored) => {
      setFormValues(restored)
    },
    [setFormValues],
  )

  const { paymentError, clearPaymentError } = useAiToolPaymentError(
    AI_TOOL_TYPES.OPPORTUNITY_ENGINE,
    handleRestoreForm,
  )

  const displayError = getFormBannerError(formik, error)
  const isSubmitting = paymentLoading || analyzing
  const { setFieldValue } = formik

  const handleCountryChange = (event) => {
    formik.handleChange(event)
    setFieldValue('state', 'any')
    setFieldValue('city', 'any')
  }

  const handleStateChange = (event) => {
    formik.handleChange(event)
    setFieldValue('city', 'any')
  }

  const craftOptions = useMemo(
    () => professionsList?.map((item) => item.title),
    [professionsList],
  )

  const contentTypeOptions = useMemo(
    () => contentTypesList?.map((item) => item.title),
    [contentTypesList],
  )

  const languageOptions = useMemo(
    () => languageList?.map((item) => item.title),
    [languageList],
  )

  const stateOptions = useMemo(() => {
    if (formik.values.country === 'any') return []
    return stateList?.map((item) => item.title)
  }, [formik.values.country, stateList])

  const cityOptions = useMemo(() => {
    if (formik.values.country === 'any' || formik.values.state === 'any') {
      return []
    }
    return cityList?.map((item) => item.title)
  }, [formik.values.country, formik.values.state, cityList])

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/dropdown/list`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setProfessionsList(response.data.data.profession || [])
          setContentTypesList(response.data.data.contentType || [])
          setLanguageList(response.data.data.languages || [])
        }
      })
      .catch((error) => {
        console.error(error)
      })
  }, [])

  useEffect(() => {
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/users/country-list`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setCountryList(response?.data?.data)
        }
      })
      .catch((error) => {
        console.error(error)
        setCountryList([])
      })
  }, [])

  useEffect(() => {
    if (formik.values.country === 'any') {
      setStateList([])
      return
    }

    const countryId = countryList?.find(
      (item) => item.title === formik.values.country,
    )?.id
    if (!countryId) {
      setStateList([])
      return
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/users/state-list/${countryId}`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setStateList(response?.data?.data)
        }
      })
      .catch((error) => {
        console.error(error)
        setStateList([])
      })
  }, [formik.values.country, countryList])

  useEffect(() => {
    if (formik.values.state === 'any') {
      setCityList([])
      return
    }

    const stateId = stateList?.find(
      (item) => item.title === formik.values.state,
    )?.id
    if (!stateId) {
      setCityList([])
      return
    }

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/users/city-list/${stateId}`,
        getAiToolApiConfig(),
      )
      .then((response) => {
        if (response?.data?.data) {
          setCityList(response?.data?.data)
        }
      })
      .catch((error) => {
        console.error(error)
        setCityList([])
      })
  }, [formik.values.state, stateList])

  useEffect(() => {
    if (result && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [result])

  const reset = () => {
    setResult(null)
    setResultInputs(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="production-tools-theme opportunity-engine-page">
      <Helmet>
        <title>Opportunity Engine — PitchBlu</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Bricolage+Grotesque:opsz,wght@12..96,200..800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <ProductionToolsHeader badge="AI Powered" />

      <section className="hero">
        <div className="eyebrow">For Film Professionals</div>
        <h1>
          Find Your Best
          <br />5 Opportunities.
        </h1>
        <p className="hsub">
          Select your required professions and filters. We scan every open post
          on PitchBlu and surface the top 5 looking for someone like you — with
          exactly how to respond to each.
        </p>
        <div className="ai-tool-hero-actions">
          <AiToolPriceBadge
            toolType={AI_TOOL_TYPES.OPPORTUNITY_ENGINE}
            variant="hero"
          />
          <AiToolSamplePdfButton toolType={AI_TOOL_TYPES.OPPORTUNITY_ENGINE} />
        </div>
        <div className="hero-feature-pills">
          {[
            'Top 5 Only',
            'ATL / BTL Format',
            'Post Link Included',
            'PDF Export',
          ].map((label) => (
            <span key={label} className="hero-feature-pill">
              {label}
            </span>
          ))}
        </div>
      </section>

      <div className="wrap">
        <div className="card">
          <AiToolPaymentErrorBanner
            message={paymentError}
            onDismiss={clearPaymentError}
          />
          <div className="frow">
            <div>
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                placeholder="example@gmail.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                name="email"
                maxLength={AI_TOOL_LIMITS.EMAIL_MAX}
              />
              <FieldError formik={formik} name="email" />
            </div>
            <div>
              <label htmlFor="uname">Full Name</label>
              <input
                id="uname"
                type="text"
                placeholder="e.g. Ravi Kumar"
                value={formik.values.uname}
                onChange={formik.handleChange}
                name="uname"
                maxLength={AI_TOOL_LIMITS.NAME_MAX}
                autoComplete="name"
              />
              <FieldError formik={formik} name="uname" />
            </div>
          </div>
          <Stack mb="22px">
            <span className="slabel">Select Your Required Professions</span>
            <ChipSelect
              options={craftOptions}
              selected={formik.values.selCrafts}
              onChange={(value) => formik.setFieldValue('selCrafts', value)}
            />
            <FieldError formik={formik} name="selCrafts" />
          </Stack>
          <Stack mb="22px">
            <span className="slabel">
              Content Type — What kind of work? (optional, select multiple)
            </span>
            <ChipSelect
              options={contentTypeOptions}
              selected={formik.values.selCT}
              onChange={(value) => formik.setFieldValue('selCT', value)}
            />
          </Stack>
          <span className="slabel">Location</span>
          <div className="frow3">
            <div>
              <label htmlFor="countryF">Country</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="countryF"
                value={formik.values.country}
                onChange={handleCountryChange}
                name="country"
              >
                <option value="any">Any Country</option>
                {countryList.map((item) => (
                  <option key={item.id} value={item.title}>
                    {item.title}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
            <div>
              <label htmlFor="stateF">State / Region</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="stateF"
                value={formik.values.state}
                onChange={handleStateChange}
                name="state"
              >
                <option value="any">Any State</option>
                {stateOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
            <div>
              <label htmlFor="cityF">City</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="cityF"
                value={formik.values.city}
                onChange={formik.handleChange}
                name="city"
              >
                <option value="any">Any City</option>
                {cityOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
          </div>

          <span className="slabel">More Filters</span>
          <div className="frow">
            <div>
              <label htmlFor="langF">Language of Project</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="langF"
                value={formik.values.lang}
                onChange={formik.handleChange}
                name="lang"
              >
                <option value="any">Any Language</option>
                {languageOptions.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
            <div>
              <label htmlFor="recF">Recency</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="recF"
                value={formik.values.recency}
                onChange={formik.handleChange}
                name="recency"
              >
                <option value="any">All Posts</option>
                <option value="new">New Only (last 3 days)</option>
                <option value="week">This Week</option>
                <option value="old">Older Posts</option>
              </ProductionToolsSelect>
            </div>
          </div>

          <div className="frow">
            <div>
              <label htmlFor="genderF">Gender (for hire posts)</label>
              <ProductionToolsSelect
                autoComplete="new-password"
                id="genderF"
                value={formik.values.gender}
                onChange={formik.handleChange}
                name="gender"
              >
                <option value="any">Any / All</option>
                {genderOptions.map((option) => (
                  <option key={option.id} value={option.name}>
                    {option.name}
                  </option>
                ))}
              </ProductionToolsSelect>
            </div>
            <div>
              <label htmlFor="ageF">Age Range (optional)</label>
              <input
                id="ageF"
                type="text"
                placeholder="e.g. 20-30 or leave blank"
                value={formik.values.age}
                onChange={formik.handleChange}
                name="age"
                maxLength={AI_TOOL_LIMITS.AGE_MAX}
              />
              <FieldError formik={formik} name="age" />
            </div>
          </div>

          {displayError && <div className="err on">{displayError}</div>}

          <AiToolRunButton loading={isSubmitting} onClick={formik.handleSubmit}>
            Find My Best 5
          </AiToolRunButton>
        </div>

        {analyzing && (
          <div className="stream-wrap">
            <div className="stream-card">
              <div className="stream-label">
                ● AI is finding your best opportunities
              </div>
              <div className="stream-dots">
                <div className="dot" />
                <div className="dot" />
                <div className="dot" />
              </div>
              <div className="stream-msg">{streamMsg}</div>
            </div>
          </div>
        )}
      </div>

      <div ref={resultsRef}>
        {result && resultInputs && (
          <OpportunityResults
            result={result}
            inputs={resultInputs}
            onReset={reset}
          />
        )}
      </div>
    </div>
  )
}

export default OpportunityEngine
